import React, { useMemo, useEffect, useState } from "react";
import { Button } from "./button";
import { Loader } from "./loader";
import { Upload, Plus } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { globalStorage } from "../../utils/globalStorage";
import { isNullOrEmpty } from "../../utils/commonUtils";
import { cloneDeep } from "lodash";

const applicationData = globalStorage.getItem("APPLICATION_DATA");

export const createDocumentResponseLocally = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: new Date().toISOString(),
      documentType,
      parentType,
      parentId
    };
  });
};

export const emptyValuedDocument = {
  documentId: null,
  documentType: "Others",
  parentType: "",
  parentId: "",
  name: "",
  path: "",
  review: {
    reason: null,
    reviewStatus: "Pending Review"
  },
  comment: null,
  type: "tag",
  status: "ACTIVE",
  fileSize: 0,
  statementDate: null
};


export const extensionList = [
  "docs",
  "docx",
  "doc",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xlsx",
  "xls"
];

export const assignDocIdAndPath = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: file.createdOn,
      documentId: file.documentId,
      path: file.path,
      documentType,
      parentType,
      parentId
    };
  });
};

const toUpperCase = (_extensionList) => {
  return _extensionList.map((item) => {
    return item.toUpperCase();
  });
};
const errorMessage = (list) => {
  const uppercaseExtensionList = toUpperCase(list);
  return `Supported file formats: ${uppercaseExtensionList
    .join(", ")
    .replace(/_/g, " ")}.`;
};

const checkDocFormat = (file, overRideFileFormat) => {
  const fileExt = file.name.split(".").pop().toLowerCase();
  const invalidFileFormat =
    (overRideFileFormat || extensionList).indexOf(fileExt) === -1;
  if (invalidFileFormat) {
    return errorMessage(overRideFileFormat || extensionList);
  }
  if (file.size > 524288000) {
    return "Max file size: 500MB";
  }
  return "";
};

export const upload = ({
  idx,
  file,
  progressInfos,
  setProgressInfos,
  loanId,
  originatorId,
  originatorLoanId,
  overRideFileFormat,
  generatePreSignedUrl,
  uploadToAwsS3
  
}) => {
  const UPLOADING_FAILED = "Uploading failed";
  const clonedProgressInfos = cloneDeep(progressInfos);
  const fileValidation = checkDocFormat(file, overRideFileFormat);
  if (!isNullOrEmpty(fileValidation)) {
    setProgressInfos((prev) => {
      const updatedPrev = [...prev];
      updatedPrev[idx].percentage = 0;
      updatedPrev[idx].message = UPLOADING_FAILED;
      updatedPrev[idx].description = fileValidation;
      updatedPrev[idx].isUploaded = false;
      return updatedPrev;
    });
    return;
  }
  generatePreSignedUrl({
    path: `loan/${loanId}/${Date.now()}/${file?.name}`,
    originatorId,
    originatorLoanId
  })
    .then((resp) => {
      const preSignedUrl = resp?.data;
      clonedProgressInfos[idx].path = preSignedUrl.split("?")[0];
      uploadToAwsS3(preSignedUrl, file, (event) => {
        setProgressInfos((prev) => {
          const updatedPrev = [...prev];
          updatedPrev[idx].percentage = Math.round(
            (100 * event.loaded) / event.total
          );
          updatedPrev[idx].path = preSignedUrl.split("?")[0];
          return updatedPrev;
        });
      })
        .then(() => {
          setProgressInfos((prev) => {
            const updatedPrev = [...prev];
            updatedPrev[idx].message = "Uploaded successfully";
            updatedPrev[idx].isUploaded = true;
            return updatedPrev;
          });
        })
        .catch(() => {
          setProgressInfos((prev) => {
            const updatedPrev = [...prev];
            updatedPrev[idx].percentage = 0;
            updatedPrev[idx].message = UPLOADING_FAILED;
            updatedPrev[idx].isUploaded = false;
            return updatedPrev;
          });
        });
    })
    .catch(() => {
      setProgressInfos((prev) => {
        const updatedPrev = [...prev];
        updatedPrev[idx].percentage = 0;
        updatedPrev[idx].message = UPLOADING_FAILED;
        updatedPrev[idx].isUploaded = false;
        return updatedPrev;
      });
    });
};
export const checkFileFormat = (docs, setErrorMsg) => {
  let validations = false;
  validations = docs.some((file) => {
    const fileExt = file.name.split(".").pop().toLowerCase();
    const invalidFileFormat = extensionList.indexOf(fileExt) === -1;
    if (file.size > 524288000 || invalidFileFormat) {
      if (invalidFileFormat) {
        setErrorMsg?.("Wrong file format");
      } else {
        setErrorMsg?.("File size is more than 500MB");
      }
      return true;
    }
    setErrorMsg?.("");
  });

  return validations;
};
const DragAndDrop = (props) => {
  const {
    loanId,
    isReadOnly,
    addMultipleDocumentsAPI={},
    storeLocalOptions = {},
    remoteFetch,
    documentType,
    parentType,
    parentId = "",
    updateLoacalContextOnUpload = false,
    checkFilesFormat = true,
    dropZoneOptions = {},
    overRideFileFormat = null,
    maxFiles = null,
    postResponseOnUploadDoc = null,
    generatePreSignedUrl,
    uploadToAwsS3,
    toastError
  } = props;

  const [prevAcceptedFiles, setPrevAcceptedFiles] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isUploding, setIsUploding] = useState(false);
  const [progressInfos, setProgressInfos] = useState([]);
  const [startUpload, setStartUpload] = useState(false);
  const [disableConfirmBtn, setDisableConfirmBtn] = useState(true);
  const [isAllFilesUploaded, setIsAllFilesUploaded] = useState(false);

  
  useEffect(() => {
      const filteredFiles = progressInfos.filter((file) => {
        return file.percentage < 100 && file.message !== "Uploaded successfully";
      });
      if (filteredFiles.length) {
        setIsUploding(filteredFiles.length > 0);
      } else {
        setIsUploding(false);
      }
    }, [progressInfos]);

  const {
    setLocalUploadedFiles = () => {},
    localUploadedFiles = [],
    localContext = { documents: [] },
    handleUpdateDocumentContext = () => {},
    contextKey = "documents"
  } = storeLocalOptions;

   const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      toastError(
        "Error",
        fileRejections?.[0]?.errors?.[0]?.message,
        ()=>{}
      )
    }
  };
    useEffect(() => {
      const filteredFiles = progressInfos.filter((file) => {
        return file.percentage < 100 && file.message !== "Uploaded successfully";
      });
      if (filteredFiles.length) {
        setIsUploding(filteredFiles.length > 0);
      } else {
        setIsUploding(false);
      }
    }, [progressInfos]);
     const { getRootProps, getInputProps, open, acceptedFiles, isDragActive } =
      useDropzone({
        noClick: false,
        disabled: isReadOnly || isUploding,
        noKeyboard: true,
        maxFiles,
        onDrop,
        ...dropZoneOptions
      });
  
    const storeFileDetailsLocally = () => {
      const fileValidations = checkFileFormat(acceptedFiles, setErrorMsg);
      if (!fileValidations) {
        const files = createDocumentResponseLocally(
          acceptedFiles,
          localContext,
          contextKey,
          documentType,
          parentType,
          parentId
        );
        const currentDocuments = [...localContext[contextKey], ...files];
        handleUpdateDocumentContext(
          contextKey,
          currentDocuments,
          "",
          localContext
        );
        const allAcceptedFilesToUpload = [...prevAcceptedFiles, ...acceptedFiles];
        setLocalUploadedFiles(allAcceptedFilesToUpload);
        setPrevAcceptedFiles(allAcceptedFilesToUpload);
      }
    };
  
    useEffect(() => {
      if (acceptedFiles.length === 0) return;
      if (checkFilesFormat) {
        const fileValidations = checkFileFormat(acceptedFiles, setErrorMsg);
        if (fileValidations) return;
      }
      if (parentId !== "") {
        const allAcceptedFilesToUpload = [
          ...localUploadedFiles,
          ...acceptedFiles
        ];
        setLocalUploadedFiles(allAcceptedFilesToUpload);
        setOpenModal(true);
        const tempProgressInfos = acceptedFiles.map((file) => {
          return {
            percentage: 0,
            fileName: file.name,
            message: "Uploading...",
            description: "",
            path: null,
            isUploaded: null
          };
        });
        setProgressInfos(tempProgressInfos);
        setStartUpload(true);
        setIsAllFilesUploaded(false);
        return;
      }
      storeFileDetailsLocally();
    }, [acceptedFiles]);
  
    useEffect(() => {
      if (!startUpload) return;
      acceptedFiles.forEach((file, idx) => {
        upload({
          idx,
          file,
          progressInfos,
          setProgressInfos,
          loanId,
          overRideFileFormat,
          generatePreSignedUrl: generatePreSignedUrl,
          uploadToAwsS3: uploadToAwsS3
        });
      
        });
      
      setStartUpload(false);
    }, [startUpload]);
  
    const isReadyToProcessUpload = () => {
      if (isAllFilesUploaded) return false;
      if (!progressInfos?.length) return false;
      return !progressInfos.some((progressInfo) => {
        return progressInfo?.isUploaded === null;
      });
    };
  
    const filterUploadedDocs = (documents) => {
      return documents?.reduce((acc, doc, index) => {
        if (progressInfos[index]?.isUploaded) {
          acc.push({
            ...doc,
            path: progressInfos[index].path
          });
        }
        return acc;
      }, []);
    };
  
    const updateProgressInfosWithApiData = (apiData) => {
      const files = cloneDeep(progressInfos);
      return files?.map((document) => {
        const filteredDoc = apiData.find((doc) => {
          return doc.path === document.path;
        });
        if (filteredDoc) {
          document.documentId = filteredDoc.documentId;
          document.createdOn = filteredDoc.createdOn;
        }
        return document;
      });
    };
  
    const handleApiSuccess = (data) => {
      if (data?.data) {
        setProgressInfos(updateProgressInfosWithApiData(data.data));
        setIsAllFilesUploaded(true);
      }
      remoteFetch();
      if (!updateLoacalContextOnUpload) return;
      const files = assignDocIdAndPath(
        data?.data,
        localContext,
        contextKey,
        documentType,
        parentType,
        parentId
      );
      const currentDocuments = [...localContext[contextKey], ...files];
      handleUpdateDocumentContext(contextKey, currentDocuments, "", localContext);
    };
  
    const handleApiError = () => {
      setOpenModal(false);
      toastError(
        "Error",
        "Error while associating uploaded document to parent.",
        ()=>{}
      );
    };
  
    useEffect(() => {
      if (!isReadyToProcessUpload()) return;
      const documents = createDocumentResponseLocally(
        acceptedFiles,
        localContext,
        contextKey,
        documentType,
        parentType,
        parentId
      );
      const updatedDocs = filterUploadedDocs(documents);
      setDisableConfirmBtn(false);
      if (updatedDocs?.length > 0) {
        if (postResponseOnUploadDoc) {
          postResponseOnUploadDoc(updatedDocs);
        } else {
          addMultipleDocumentsAPI.mutate(updatedDocs, {
            onSuccess: handleApiSuccess,
            onError: handleApiError
          });
        }
      }
    }, [progressInfos]);
  
    const handleClose = (event, reason) => {
      if (reason && reason === "backdropClick") return;
      setOpenModal(false);
    };
  
    const isLoading = useMemo(() => {
      return addMultipleDocumentsAPI.isLoading;
    }, [addMultipleDocumentsAPI.isLoading]);    
    const fileLabel = maxFiles === 1 ? "File" : "Files";
    const uploadFilesLimitText = `Upload Files Limit: ${maxFiles} ${fileLabel} |`;
  return (
    <>
    {isLoading && <Loader />}
    <div> 
      <div className='min-w-100 p-6 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer bg-[var(--color-neutral-1)]'>
        <div {...getRootProps()} className='flex flex-col gap-4'>
          <div className='flex flex-col items-center rounded'>
            <div className='w-[80px] h-[80px] rounded-full flex items-center justify-center bg-[var(--background-page)]'>
              <Upload className='w-5 h-5 text-[var(--color-primary-3)]' />
            </div>
          </div>
          <div className='flex flex-col items-center gap-2'>
            <p className="semi-bold text-base">Drag & drop files here </p>
            <p className="text-xs text-gray-600">or click the button to browse your files</p>
          </div>
          <div>
              <Button variant='primary'>
                  Add Files
                  <Plus className='w-5 h-5' />
              </Button>
              <input type='file' {...getInputProps()}/>
            </div>
          <div className='flex flex-col items-center'>
          <p className="text-xs text-[var(--color-neutral-8)]">PDF, DOC, XLS, JPG, PNG (Max 500MB)</p>
          </div>
        </div>
      </div>
       <div className='text-red-500 text-sm mt-2'>
        {errorMsg}
      </div>
    </div>
   
  </>
  );
}
export { DragAndDrop };